import { MouseEvent, ChangeEvent, FC } from 'react'
import { useSelector } from 'react-redux'
import AdUnitsIcon from '@mui/icons-material/AdUnits'
import DvrIcon from '@mui/icons-material/Dvr'

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import { colors } from 'ThemeStyle'
import Tooltip from '@mui/material/Tooltip'

import moment from 'moment'

import Chip from '../../chip'

import { ColorTypes } from 'components/chip/Styles.elements'
import { RootState } from 'dataStore/state'
import { IOrder, IOrderStatus } from 'dto/orders.dto'

import { Wrapper } from './Styles.elements'
import EnhancedTableHead, { HeadCell, Order } from './EnhancedTableHead'
import { useActions } from 'hooks/useActions'

interface IOrderTable {
  setState: Function
  orders: IOrder[]
}

export enum ColorTypesRow {
  'Не оплачено' = 'opacity_orange',
  'Отменено' = 'opacity_red',
  'Отклонен' = 'opacity_red',
  'Оплачено' = 'opacity_green',
  'Оплачен' = 'opacity_green',
  'Перевод' = 'opacity_yellow',
  'Наличные' = 'opacity_light_blue',
  'Оплата картой' = 'opacity_purple',
  'Ожидает подтверждения' = 'opacity_gray',
}

/**
 * Таблица заказов: данные и сортировка приходят с бэкенда (одна страница).
 * @param orders — текущая страница заказов
 * @param setState — открытие деталей заказа
 */
const OrderTable: FC<IOrderTable> = ({ orders, setState }) => {
  const { pagination, ordersTotal, sortBy, sortOrder } = useSelector(
    (state: RootState) => state.orders
  )
  const { setPage, setRowsPerPage, setOrdersSort } = useActions()

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
  }

  /**
   * Запрашивает серверную сортировку.
   * @param _event — клик
   * @param property — поле сортировки
   */
  const handleRequestSort = (
    _event: MouseEvent<unknown>,
    property: keyof IOrder
  ) => {
    const isAsc = sortBy === property && sortOrder === 'asc'
    setOrdersSort({
      sortBy: String(property),
      sortOrder: isAsc ? 'desc' : 'asc',
    })
  }

  const headCell: HeadCell[] = [
    {
      id: 'id',
      numeric: false,
      disablePadding: false,
      label: '№',
    },
    {
      id: 'pizzeria_id',
      numeric: false,
      disablePadding: false,
      label: 'Отделение',
    },
    {
      id: 'create_at',
      numeric: false,
      disablePadding: false,
      label: 'Создан',
    },
    {
      id: 'delivery_type',
      numeric: false,
      disablePadding: false,
      label: 'Тип',
    },
    {
      id: 'total_goods',
      numeric: false,
      disablePadding: false,
      label: 'Товаров',
    },
    {
      id: 'total_price',
      numeric: false,
      disablePadding: false,
      label: 'Сумма',
    },
    {
      id: 'payment_type',
      numeric: false,
      disablePadding: false,
      label: 'Тип платежа',
    },
    {
      id: 'payment_status',
      numeric: false,
      disablePadding: false,
      label: 'Статус платежа',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Статус',
    },
    {
      id: 'is_mobile',
      numeric: false,
      disablePadding: false,
      label: 'Источник',
    },
  ]

  const tableOrder: Order = sortOrder === 'asc' ? 'asc' : 'desc'

  return (
    <>
      <Wrapper>
        <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Table
            sx={{ minWidth: { xs: 560, sm: 650 } }}
            aria-label='orders table'
          >
            <EnhancedTableHead
              order={tableOrder}
              orderBy={sortBy as keyof IOrder}
              onRequestSort={handleRequestSort}
              rowCount={orders?.length ?? 0}
              headCells={headCell}
            />
            <TableBody>
              {orders?.map(row => {
                const statusColor = (status: string): string => {
                  if (status in ColorTypesRow) {
                    return colors[
                      ColorTypesRow[
                        status as keyof typeof ColorTypesRow
                      ] as keyof typeof colors
                    ]
                  }
                  return 'ffffff'
                }
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      backgroundColor: statusColor(row.payment_status),
                    }}
                    onClick={() => setState(row)}
                  >
                    <TableCell component='th' scope='row'>
                      {row.id}
                    </TableCell>
                    <TableCell align='right'>
                      {row?.pizzeria?.address?.city}{' '}
                      {row?.pizzeria?.address?.street}
                    </TableCell>
                    <TableCell align='right'>
                      {moment(row?.create_at).format('LT')}
                    </TableCell>
                    <TableCell align='right'>
                      <DeliveryHandle order={row} />
                    </TableCell>
                    <TableCell align='right'>{row?.total_goods} шт</TableCell>
                    <TableCell align='right'>
                      <span style={{ fontWeight: 'bold' }}>
                        {row?.total_price} ₽
                      </span>
                    </TableCell>
                    <TableCell align='right'>{row?.payment_type}</TableCell>
                    <TableCell align='right'>{row?.payment_status}</TableCell>
                    <TableCell align='right'>
                      <StatusHandler status={row?.status_obj} />
                    </TableCell>
                    <TableCell align='right'>
                      {row?.is_mobile ? (
                        <Tooltip title='Заказ из приложения'>
                          <AdUnitsIcon color='primary' />
                        </Tooltip>
                      ) : (
                        <Tooltip title='Заказ с сайта'>
                          <DvrIcon />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30]}
          component='div'
          count={Number(ordersTotal)}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage='Строк на странице:'
        />
      </Wrapper>
    </>
  )
}

export default OrderTable

const DeliveryHandle: FC<{ order: IOrder }> = ({ order }) => {
  if (order?.delivery_type) {
    if (order?.in_time) {
      return (
        <div style={{ color: 'red' }}>
          {moment(order?.in_time).format('L')} -{' '}
          {moment(order?.in_time).format('LT')}
        </div>
      )
    } else {
      return <>Доставка</>
    }
  } else {
    return <>Самовывоз</>
  }
}

const StatusHandler: FC<{ status: IOrderStatus | undefined }> = ({
  status,
}) => {
  if (!status) {
    return <Chip color={ColorTypes['gray']}>Не задан</Chip>
  } else {
    return (
      <Chip color={ColorTypes[status?.color as keyof typeof ColorTypes]}>
        {status?.description}
      </Chip>
    )
  }
}
